import { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import userIcon from '../../assets/sidebar/icons/User.svg';
import logo from '../../assets/alkemy/logo.png';
import logoutIcon from '../../assets/header/logout-icon.svg';
import menuBurger from '../../assets/header/menu-burger.svg';
import { t } from '../../i18n/i18n';
import Dropdown from '../Dropdown/Dropdown';
import DebouncedButton from '../Button/Button';
import { HOME, RoutesKeys } from '../../constants/routes';
import useSidebar from '../../hooks/useSidebar';
import DropdownLanguage from '../DropdownLanguage/DropdownLanguage';
import { Window } from '../../utils/window';
import { auth0Client } from '../../auth0/auth0';
import { clearLocalStorageOnLogout } from '../../utils/localStorageUtils';
import { setRefs } from '../../redux/actions/uxActions';
import CalendarBasicIcon from '../../assets/login/CalendarBasicIcon';
import Video from '../../assets/header/Video';
import DropdownNotification from '../DropdownNotification/DropdownNotification';
import OnboardingIcon from '../../assets/header/Onboarding';
import useWhiteLabelSidebar from '../../whiteLabel/useWhiteLabelSidebar';
import settingsIcon from '../../assets/header/settings-icon.svg';
import 'intro.js/minified/introjs.min.css';
import './Header.scss';
import WorkspaceDropdown from '../WorkspaceDropdown/WorkspaceDropdown';

const Header = ({ approved = true, maintenance, open, setOpen }) => {
  const profile = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const history = useHistory();
  const { collapsed, setCollapsed } = useSidebar();
  const dropdown = useRef(null);
  const { routes } = useWhiteLabelSidebar();

  const getConfigurationRoutes = (routes) => {
    const configurationRoutes = routes.find((route) => route.key === RoutesKeys.CONFIGURATION);
    const routesMapped = configurationRoutes?.subRoutes?.map((subRoute) => {
      return {
        icon: subRoute.icon ?? settingsIcon,
        title: t(subRoute.label),
        className: 'dropdown-text  font-weight-bold text-xs',
        link: subRoute.path,
        id: subRoute.label,
      };
    });
    return routesMapped ?? [];
  };

  useEffect(() => {
    dispatch(setRefs({ dropdown }));
  }, [dropdown]);

  return (
    <header className="header d-flex align-items-center justify-content-between flex-wrap px-xs-2 px-1">
      <div className="d-flex align-items-center flex-wrap py-1">
        <Link to={HOME}>
          <img src={logo} className="logo mr-sm-4" alt={t('HEADER_LOGO')} />
        </Link>
        <div className="d-none d-md-inline header-vertical-line mr-sm-4" />
      </div>
      {!maintenance &&
        (approved ? (
          <div className="d-flex py-1 align-self-center align-self-sm-center align-content-center header-dropdown-max-height">
            <div
              className="d-flex justify-content-center align-items-center"
              id="HEADER_HELP_LINKS"
            >
              <a
                className="schedule-demo-btn-container hoverable-btn mb-1"
                target="_blank"
                rel="noreferrer"
                href={process.env.RAZZLE_RUNTIME_SALES_CALENDAR_URL}
              >
                <CalendarBasicIcon
                  className="calendar-basic-icon-margin"
                  fillClassname="calendar-basic-icon hoverable-btn"
                />
                <span className="d-none d-md-inline schedule-demo-btn  text-xs font-weight-700 ml-2 hoverable-btn">
                  {t('SCHEDULE_MEETING_ACTION')}
                </span>
              </a>
              <a
                className="schedule-demo-btn-container hoverable-btn ml-3"
                target="_blank"
                rel="noreferrer"
                href={process.env.RAZZLE_RUNTIME_DEMO_TUTORIALS_URL}
              >
                <Video classNames="mt-2 calendar-basic-icon hoverable-btn" />
                <span className="d-none d-md-inline schedule-demo-btn  text-xs font-weight-700 ml-1 hoverable-btn">
                  {t('TUTORIALS_ACTION')}
                </span>
              </a>
              <button
                type="button"
                className="schedule-demo-btn-container hoverable-btn ml-3 demo-onboarding"
                onClick={() => {
                  localStorage.setItem('isOnboarding', 'true');
                  history.push('/');
                }}
              >
                <OnboardingIcon classNames="mt-2 demo-icon hoverable-btn" />
                <span className="d-none d-md-inline schedule-demo-btn  text-xs font-weight-700 ml-1">
                  {t('DEMO_ACTION')}
                </span>
              </button>
            </div>
            <div className="align-self-center">
              <DropdownLanguage className="dashboard-groups-select mx-3 text-xs" />
            </div>
            <div className="align-self-center">
              <DropdownNotification className="dashboard-groups-select mx-1" />
            </div>
            <Dropdown
              className="dashboard-groups-select d-none d-xl-block px-2 text-xs mt-1"
              placeholder={t('HEADER_SEE_ANOTHER_GROUP')}
              open={open}
              setOpen={setOpen}
              options={[
                {
                  icon: userIcon,
                  title: t('MENU_ITEM_PROFILE'),
                  className: 'dropdown-text  font-weight-bold text-xs',
                  link: '/profile',
                  ref: dropdown,
                  id: 'DROPDOWN',
                },
                ...getConfigurationRoutes(routes),
                {
                  icon: logoutIcon,
                  title: t('BUTTON_LOGOUT'),
                  className: 'dropdown-text  font-weight-bold text-xs',
                  action: () => {
                    if (Window()) {
                      clearLocalStorageOnLogout();
                      auth0Client().logout({ returnTo: window.location.origin });
                    }
                  },
                },
              ]}
              profile={profile}
            />
            <img
              src={menuBurger}
              aria-hidden="true"
              onClick={() => setCollapsed(!collapsed)}
              className="d-block d-xl-none cursor-pointer p-2"
              alt={t('HEADER_MENU')}
            />
          </div>
        ) : (
          <DebouncedButton
            text={t('BUTTON_LOGOUT')}
            onClick={() => {
              if (Window()) {
                clearLocalStorageOnLogout();
                auth0Client().logout({ returnTo: window.location.origin });
              }
            }}
            load={false}
          />
        ))}
    </header>
  );
};
export default Header;
